import React, { useState, useEffect } from 'react';
import {
  Container, Row, Col, Card, Form, Button, Table,
  Modal, Badge, Alert, Spinner, Image, Dropdown, ButtonGroup
} from 'react-bootstrap';
import axios from 'axios';
import AdminNavbar from './components/AdminNavbar';
import { BASE_URL, UPLOAD_BASE_URL } from '../../services/api';

const TeamMembersManager = () => {
  const [members, setMembers] = useState([]);
  const [departments, setDepartments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [user, setUser] = useState(null);

  // Modal states
  const [showModal, setShowModal] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [editingMember, setEditingMember] = useState(null);
  const [deletingMember, setDeletingMember] = useState(null);
  
  // Image states
  const [imagePreview, setImagePreview] = useState(null);
  const [uploadingImage, setUploadingImage] = useState(false);
  
  // Form data
  const [formData, setFormData] = useState({
    name: '',
    designation: '',
    department: '',
    image_url: '',
    bio: '',
    email: '',
    phone: '',
    display_order: 0,
    is_active: true
  });

  const API_BASE_URL = BASE_URL;

  useEffect(() => {
    const userData = JSON.parse(localStorage.getItem('user') || 'null');
    setUser(userData);
    fetchMembers();
    fetchDepartments();
  }, []);

  const fetchMembers = async () => {
    try {
      setLoading(true);
      setError('');
      const token = localStorage.getItem('token');
      const res = await axios.get(`${API_BASE_URL}/team_members.php?admin=true`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      if (res.data.success) {
        // Ensure is_active is boolean
        const formattedMembers = res.data.data.map(member => ({
          ...member,
          is_active: Boolean(member.is_active)
        }));
        setMembers(formattedMembers);
      } else {
        setError(res.data.message || 'Failed to load team members');
      }
    } catch (err) {
      console.error('Fetch members error:', err);
      setError('Failed to load team members. Please check your connection.');
    } finally {
      setLoading(false);
    }
  };

  const fetchDepartments = async () => {
    try {
      const token = localStorage.getItem('token');
      const res = await axios.get(`${API_BASE_URL}/team_members.php?departments=true`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      if (res.data.success) {
        setDepartments(res.data.data);
      }
    } catch (err) {
      console.error('Fetch departments error:', err);
    }
  };

  const resetForm = () => {
    setFormData({
      name: '',
      designation: '',
      department: '',
      image_url: '',
      bio: '',
      email: '',
      phone: '',
      display_order: 0,
      is_active: true
    });
    setImagePreview(null);
    setEditingMember(null);
  };

  const handleInputChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData(prev => ({ 
      ...prev, 
      [name]: type === 'checkbox' ? checked : value 
    }));
  };

  const handleTextareaChange = (e) => {
    setFormData(prev => ({ ...prev, [e.target.name]: e.target.value }));
  };

  const handleImageChange = (e) => {
    const file = e.target.files[0];
    if (!file) return;
    
    // Check file size (max 5MB)
    if (file.size > 5 * 1024 * 1024) {
      setError('Image size should be less than 5MB');
      e.target.value = '';
      return;
    }
    
    // Check file type
    const validTypes = ['image/jpeg', 'image/jpg', 'image/png', 'image/gif', 'image/webp'];
    if (!validTypes.includes(file.type)) {
      setError('Invalid file type. Please upload JPG, PNG, GIF, or WebP image.');
      e.target.value = '';
      return;
    }
    
    setFormData(prev => ({ ...prev, image_file: file, image_url: '' }));
    setImagePreview(URL.createObjectURL(file));
    setError('');
  };

  const removeImage = () => {
    setFormData(prev => ({ ...prev, image_url: '', image_file: null }));
    setImagePreview(null);
  };

  const uploadImage = async (file) => {
    const token = localStorage.getItem('token');
    const data = new FormData();
    data.append('image', file);
    setUploadingImage(true);

    try {
      const res = await axios.post(`${API_BASE_URL}/upload_team_image.php`, data, {
        headers: { 
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'multipart/form-data'
        }
      });
      
      if (res.data.success) {
        return res.data.image_url;
      } else {
        setError(res.data.message || 'Image upload failed');
        return null;
      }
    } catch (err) {
      console.error('Upload image error:', err);
      setError('Failed to upload image. Please try again.');
      return null;
    } finally {
      setUploadingImage(false);
    }
  };

  const handleEdit = (member) => {
    setEditingMember(member);
    setFormData({
      name: member.name || '',
      designation: member.designation || '',
      department: member.department || '',
      image_url: member.image_url || '',
      bio: member.bio || '',
      email: member.email || '',
      phone: member.phone || '',
      display_order: member.display_order || 0,
      is_active: Boolean(member.is_active) // Ensure boolean
    });
    
    if (member.image_url) {
      setImagePreview(`${UPLOAD_BASE_URL}${member.image_url}`);
    } else {
      setImagePreview(null);
    }
    
    setShowModal(true);
  };

  const handleDeleteClick = (member) => {
    setDeletingMember(member);
    setShowDeleteModal(true);
  };

  const handleDelete = async () => {
    if (!deletingMember) return;
    
    try {
      setError('');
      const token = localStorage.getItem('token');
      const res = await axios.delete(`${API_BASE_URL}/team_members.php?id=${deletingMember.id}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      if (res.data.success) {
        setSuccess('Team member deleted successfully');
        fetchMembers();
        setShowDeleteModal(false);
        setDeletingMember(null);
      } else {
        setError(res.data.message || 'Failed to delete team member');
      }
    } catch (err) {
      console.error('Delete error:', err);
      setError('Failed to delete team member. Please try again.');
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setSuccess('');

    // Validation
    if (!formData.name.trim()) {
      setError('Name is required');
      return;
    }
    
    if (!formData.designation.trim()) {
      setError('Designation is required');
      return;
    }

    try {
      let data = { ...formData };
      delete data.image_file;

      // Ensure is_active is boolean for backend
      data.is_active = Boolean(data.is_active);

      // Upload new image if selected
      if (formData.image_file) {
        const uploadedUrl = await uploadImage(formData.image_file);
        if (!uploadedUrl) return; // Error already set by uploadImage
        data.image_url = uploadedUrl;
      }

      const token = localStorage.getItem('token');
      let res;

      if (editingMember) {
        // Update existing member
        res = await axios.put(`${API_BASE_URL}/team_members.php?id=${editingMember.id}`, data, {
          headers: { 
            Authorization: `Bearer ${token}`,
            'Content-Type': 'application/json'
          }
        });
      } else {
        // Create new member
        res = await axios.post(`${API_BASE_URL}/team_members.php`, data, {
          headers: { 
            Authorization: `Bearer ${token}`,
            'Content-Type': 'application/json'
          }
        });
      }

      if (res.data.success) {
        setSuccess(editingMember ? 'Team member updated successfully' : 'Team member added successfully');
        setShowModal(false);
        resetForm();
        fetchMembers();
      } else {
        setError(res.data.message || (editingMember ? 'Failed to update team member' : 'Failed to add team member'));
      }
    } catch (err) {
      console.error('Save error:', err);
      setError('An error occurred. Please try again.');
    }
  };

  // Toggle active status - Fixed version
  const toggleStatus = async (member) => {
    try {
      setError('');
      setSuccess('');
      const token = localStorage.getItem('token');
      
      // Create updated data - ensure is_active is boolean
      const updatedData = {
        ...member,
        is_active: !Boolean(member.is_active) // Toggle boolean value
      };
      
      console.log('Toggling status for:', member.name, 'New status:', updatedData.is_active);
      
      const res = await axios.put(`${API_BASE_URL}/team_members.php?id=${member.id}`, updatedData, {
        headers: { 
          Authorization: `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });
      
      if (res.data.success) {
        setSuccess(`Team member ${member.is_active ? 'deactivated' : 'activated'} successfully`);
        fetchMembers(); // Refresh the list
      } else {
        setError(res.data.message || 'Failed to update status');
      }
    } catch (err) {
      console.error('Toggle status error:', err);
      setError('Failed to update status. Please try again.');
    }
  };

  // Alternative toggle status using dedicated endpoint (if available)
  const toggleStatusAlt = async (member) => {
    try {
      setError('');
      setSuccess('');
      const token = localStorage.getItem('token');
      
      const res = await axios.patch(
        `${API_BASE_URL}/team_members.php?id=${member.id}&action=toggle-status`, 
        {},
        {
          headers: { 
            Authorization: `Bearer ${token}`,
            'Content-Type': 'application/json'
          }
        }
      );
      
      if (res.data.success) {
        const newStatus = res.data.new_status === 1;
        setSuccess(`Team member ${newStatus ? 'activated' : 'deactivated'} successfully`);
        fetchMembers();
      } else {
        setError(res.data.message || 'Failed to update status');
      }
    } catch (err) {
      console.error('Toggle status error:', err);
      // Fall back to regular PUT method
      toggleStatus(member);
    }
  };

  const handleCloseModal = () => {
    setShowModal(false);
    resetForm();
  };

  const handleCustomDepartment = (value) => {
    if (value === 'custom') {
      setFormData(prev => ({ ...prev, department: '' }));
    }
  };

  if (loading) {
    return (
      <>
        <AdminNavbar />
        <Container className="text-center mt-5 py-5">
          <Spinner animation="border" variant="primary" />
          <p className="mt-3">Loading team members...</p>
        </Container>
      </>
    );
  }

  return (
    <>
      <AdminNavbar />
      <Container fluid className="py-4">
        <Row className="mb-4 align-items-center">
          <Col>
            <h2 className="mb-0">Team Members Manager</h2>
            <p className="text-muted mb-0">Manage your team members and their details</p>
          </Col>
          <Col className="text-end">
            <Button 
              variant="primary" 
              onClick={() => {
                resetForm();
                setShowModal(true);
              }}
            >
              <i className="fas fa-plus me-2"></i>
              Add New Member
            </Button>
          </Col>
        </Row>

        {/* Alerts */}
        {error && (
          <Alert variant="danger" dismissible onClose={() => setError('')} className="mb-4">
            <i className="fas fa-exclamation-circle me-2"></i>
            {error}
          </Alert>
        )}
        
        {success && (
          <Alert variant="success" dismissible onClose={() => setSuccess('')} className="mb-4">
            <i className="fas fa-check-circle me-2"></i>
            {success}
          </Alert>
        )}

        {/* Team Members Table */}
        <Card className="border-0 shadow-sm">
          <Card.Body className="p-0">
            <div className="table-responsive">
              <Table hover className="mb-0">
                <thead className="bg-light">
                  <tr>
                    <th style={{ width: '80px' }}>Photo</th>
                    <th>Name</th>
                    <th>Designation</th>
                    <th>Department</th>
                    <th>Contact</th>
                    <th style={{ width: '100px' }}>Order</th>
                    <th style={{ width: '120px' }}>Status</th>
                    <th style={{ width: '120px' }} className="text-end">Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {members.length === 0 ? (
                    <tr>
                      <td colSpan="8" className="text-center py-5">
                        <i className="fas fa-users fa-3x text-muted mb-3"></i>
                        <p className="text-muted">No team members found</p>
                        <Button 
                          variant="outline-primary" 
                          size="sm"
                          onClick={() => {
                            resetForm();
                            setShowModal(true);
                          }}
                        >
                          <i className="fas fa-plus me-1"></i>
                          Add Your First Team Member
                        </Button>
                      </td>
                    </tr>
                  ) : (
                    members.map((member) => (
                      <tr key={member.id}>
                        <td>
                          <div className="d-flex align-items-center">
                            {member.image_url ? (
                              <Image 
                                src={`${UPLOAD_BASE_URL}${member.image_url}`} 
                                roundedCircle 
                                width={50} 
                                height={50}
                                style={{ objectFit: 'cover' }}
                                className="border"
                                alt={member.name}
                              />
                            ) : (
                              <div className="rounded-circle bg-secondary d-flex align-items-center justify-content-center" 
                                   style={{ width: '50px', height: '50px' }}>
                                <i className="fas fa-user text-white"></i>
                              </div>
                            )}
                          </div>
                        </td>
                        <td>
                          <div>
                            <strong className="d-block">{member.name}</strong>
                            {member.bio && (
                              <small className="d-block text-muted mt-1 text-truncate" style={{ maxWidth: '200px' }}>
                                {member.bio.length > 60 ? `${member.bio.substring(0, 60)}...` : member.bio}
                              </small>
                            )}
                          </div>
                        </td>
                        <td>
                          <span className="fw-medium">{member.designation}</span>
                        </td>
                        <td>
                          {member.department ? (
                            <Badge bg="info" className="text-uppercase px-2 py-1">{member.department}</Badge>
                          ) : (
                            <span className="text-muted">-</span>
                          )}
                        </td>
                        <td>
                          <div className="small">
                            {member.email && (
                              <div className="text-truncate" style={{ maxWidth: '150px' }}>
                                <i className="fas fa-envelope me-1 text-muted"></i>
                                {member.email}
                              </div>
                            )}
                            {member.phone && (
                              <div>
                                <i className="fas fa-phone me-1 text-muted"></i>
                                {member.phone}
                              </div>
                            )}
                          </div>
                        </td>
                        <td>
                          <Badge bg="secondary" className="px-2 py-1">{member.display_order}</Badge>
                        </td>
                        <td>
                          <Button 
                            variant={member.is_active ? "success" : "secondary"} 
                            size="sm"
                            onClick={() => toggleStatus(member)}
                            className="px-3 w-100"
                          >
                            <i className={`fas ${member.is_active ? 'fa-check-circle' : 'fa-times-circle'} me-1`}></i>
                            {member.is_active ? 'Active' : 'Inactive'}
                          </Button>
                        </td>
                        <td className="text-end">
                          <Dropdown as={ButtonGroup} size="sm">
                            <Button 
                              variant="outline-primary"
                              onClick={() => handleEdit(member)}
                              title="Edit"
                            >
                              <i className="fas fa-edit"></i>
                            </Button>
                            <Dropdown.Toggle split variant="outline-primary" />
                            <Dropdown.Menu align="end">
                              <Dropdown.Item onClick={() => handleEdit(member)}>
                                <i className="fas fa-edit me-2"></i>
                                Edit Details
                              </Dropdown.Item>
                              <Dropdown.Item onClick={() => toggleStatus(member)}>
                                <i className={`fas ${member.is_active ? 'fa-user-slash' : 'fa-user-check'} me-2`}></i>
                                {member.is_active ? 'Deactivate' : 'Activate'}
                              </Dropdown.Item>
                              <Dropdown.Divider />
                              <Dropdown.Item 
                                className="text-danger" 
                                onClick={() => handleDeleteClick(member)}
                              >
                                <i className="fas fa-trash me-2"></i>
                                Delete
                              </Dropdown.Item>
                            </Dropdown.Menu>
                          </Dropdown>
                        </td>
                      </tr>
                    ))
                  )}
                </tbody>
              </Table>
            </div>
          </Card.Body>
          {members.length > 0 && (
            <Card.Footer className="bg-light text-muted">
              <Row className="align-items-center">
                <Col>
                  Showing {members.length} team member{members.length !== 1 ? 's' : ''}
                </Col>
                <Col className="text-end">
                  <small>
                    <i className="fas fa-info-circle me-1"></i>
                    Click status button to activate/deactivate
                  </small>
                </Col>
              </Row>
            </Card.Footer>
          )}
        </Card>

        {/* Add/Edit Member Modal */}
        <Modal show={showModal} onHide={handleCloseModal} size="lg" centered backdrop="static">
          <Modal.Header closeButton className="bg-light">
            <Modal.Title>
              <i className={`fas ${editingMember ? 'fa-edit' : 'fa-user-plus'} me-2`}></i>
              {editingMember ? 'Edit Team Member' : 'Add New Team Member'}
            </Modal.Title>
          </Modal.Header>
          <Form onSubmit={handleSubmit}>
            <Modal.Body>
              <Row>
                <Col md={4}>
                  <Card className="border-0 shadow-sm mb-3">
                    <Card.Body className="text-center p-4">
                      {imagePreview ? (
                        <>
                          <Image 
                            src={imagePreview} 
                            roundedCircle 
                            fluid 
                            className="mb-3 border"
                            style={{ width: '150px', height: '150px', objectFit: 'cover' }}
                            alt="Preview"
                          />
                          <div className="d-flex justify-content-center gap-2">
                            <Form.Group controlId="formImage" className="mb-0">
                              <Form.Label className="btn btn-sm btn-outline-primary mb-0">
                                <i className="fas fa-camera me-1"></i>
                                Change
                                <Form.Control 
                                  type="file" 
                                  onChange={handleImageChange} 
                                  accept="image/*" 
                                  className="d-none"
                                />
                              </Form.Label>
                            </Form.Group>
                            <Button 
                              variant="outline-danger" 
                              size="sm"
                              onClick={removeImage}
                              title="Remove photo"
                            >
                              <i className="fas fa-trash"></i>
                            </Button>
                          </div>
                        </>
                      ) : (
                        <>
                          <div className="rounded-circle bg-light border d-flex align-items-center justify-content-center mx-auto mb-3" 
                               style={{ width: '150px', height: '150px' }}>
                            <i className="fas fa-user fa-3x text-muted"></i>
                          </div>
                          <Form.Group controlId="formImage">
                            <Form.Label className="btn btn-outline-primary mb-0">
                              <i className="fas fa-upload me-1"></i>
                              Upload Photo
                              <Form.Control 
                                type="file" 
                                onChange={handleImageChange} 
                                accept="image/*" 
                                className="d-none"
                              />
                            </Form.Label>
                          </Form.Group>
                        </>
                      )}
                      {uploadingImage && (
                        <div className="mt-3">
                          <Spinner animation="border" size="sm" variant="primary" />
                          <small className="ms-2 text-muted">Uploading...</small>
                        </div>
                      )}
                      <small className="d-block text-muted mt-3">
                        <i className="fas fa-info-circle me-1"></i>
                        Max 5MB. Allowed: JPG, PNG, GIF, WebP
                      </small>
                    </Card.Body>
                  </Card>
                </Col>
                <Col md={8}>
                  <Row>
                    <Col md={6}>
                      <Form.Group className="mb-3">
                        <Form.Label>
                          <strong>Full Name</strong> <span className="text-danger">*</span>
                        </Form.Label>
                        <Form.Control 
                          name="name" 
                          value={formData.name} 
                          onChange={handleInputChange}
                          placeholder="Enter full name"
                          required
                          className="py-2"
                        />
                      </Form.Group>
                    </Col>
                    <Col md={6}>
                      <Form.Group className="mb-3">
                        <Form.Label>
                          <strong>Designation</strong> <span className="text-danger">*</span>
                        </Form.Label>
                        <Form.Control 
                          name="designation" 
                          value={formData.designation} 
                          onChange={handleInputChange}
                          placeholder="e.g., Project Manager"
                          required
                          className="py-2"
                        />
                      </Form.Group>
                    </Col>
                  </Row>
                  
                  <Row>
                    <Col md={6}>
                      <Form.Group className="mb-3">
                        <Form.Label><strong>Department</strong></Form.Label>
                        <Form.Select 
                          name="department" 
                          value={formData.department || ''} 
                          onChange={(e) => {
                            handleInputChange(e);
                            handleCustomDepartment(e.target.value);
                          }}
                          className="py-2"
                        >
                          <option value="">Select Department</option>
                          {departments.map((dept, index) => (
                            <option key={index} value={dept}>{dept}</option>
                          ))}
                          <option value="custom">Add Custom Department</option>
                        </Form.Select>
                        {formData.department === '' && (
                          <Form.Control 
                            type="text" 
                            placeholder="Enter department name"
                            onChange={(e) => setFormData(prev => ({ ...prev, department: e.target.value }))}
                            className="mt-2 py-2"
                          />
                        )}
                      </Form.Group>
                    </Col>
                    <Col md={6}>
                      <Form.Group className="mb-3">
                        <Form.Label><strong>Display Order</strong></Form.Label>
                        <Form.Control 
                          type="number" 
                          name="display_order" 
                          value={formData.display_order} 
                          onChange={handleInputChange}
                          min="0"
                          placeholder="Order in listing"
                          className="py-2"
                        />
                        <Form.Text className="text-muted">
                          Lower numbers appear first
                        </Form.Text>
                      </Form.Group>
                    </Col>
                  </Row>
                  
                  <Row>
                    <Col md={6}>
                      <Form.Group className="mb-3">
                        <Form.Label><strong>Email Address</strong></Form.Label>
                        <Form.Control 
                          type="email" 
                          name="email" 
                          value={formData.email} 
                          onChange={handleInputChange}
                          placeholder="team.member@example.com"
                          className="py-2"
                        />
                      </Form.Group>
                    </Col>
                    <Col md={6}>
                      <Form.Group className="mb-3">
                        <Form.Label><strong>Phone Number</strong></Form.Label>
                        <Form.Control 
                          type="tel" 
                          name="phone" 
                          value={formData.phone} 
                          onChange={handleInputChange}
                          placeholder="+1 (555) 123-4567"
                          className="py-2"
                        />
                      </Form.Group>
                    </Col>
                  </Row>
                  
                  <Form.Group className="mb-3">
                    <Form.Label><strong>Bio / Description</strong></Form.Label>
                    <Form.Control 
                      as="textarea" 
                      rows={3}
                      name="bio" 
                      value={formData.bio} 
                      onChange={handleTextareaChange}
                      placeholder="Brief description about the team member..."
                      maxLength={500}
                      className="py-2"
                    />
                    <div className="d-flex justify-content-between align-items-center mt-1">
                      <Form.Text className="text-muted">
                        Brief introduction about the team member
                      </Form.Text>
                      <small className={`${formData.bio.length > 450 ? 'text-warning' : 'text-muted'}`}>
                        {formData.bio.length}/500 characters
                      </small>
                    </div>
                  </Form.Group>
                  
                  <Form.Check
                    type="switch"
                    id="active-switch"
                    label={<strong>Active Member</strong>}
                    name="is_active"
                    checked={Boolean(formData.is_active)}
                    onChange={handleInputChange}
                    className="mb-3 py-2"
                  />
                </Col>
              </Row>
            </Modal.Body>
            <Modal.Footer className="bg-light">
              <Button variant="outline-secondary" onClick={handleCloseModal}>
                Cancel
              </Button>
              <Button 
                type="submit" 
                variant="primary" 
                disabled={uploadingImage}
                className="px-4"
              >
                {uploadingImage ? (
                  <>
                    <Spinner animation="border" size="sm" className="me-2" />
                    Saving...
                  </>
                ) : (
                  <>
                    <i className={`fas ${editingMember ? 'fa-save' : 'fa-check'} me-2`}></i>
                    {editingMember ? 'Update Member' : 'Add Member'}
                  </>
                )}
              </Button>
            </Modal.Footer>
          </Form>
        </Modal>

        {/* Delete Confirmation Modal */}
        <Modal show={showDeleteModal} onHide={() => setShowDeleteModal(false)} centered>
          <Modal.Header closeButton className="border-0">
            <Modal.Title className="text-danger">
              <i className="fas fa-exclamation-triangle me-2"></i>
              Confirm Deletion
            </Modal.Title>
          </Modal.Header>
          <Modal.Body className="py-4">
            {deletingMember && (
              <>
                <div className="text-center mb-4">
                  <div className="rounded-circle bg-danger bg-opacity-10 d-inline-flex align-items-center justify-content-center mb-3" 
                       style={{ width: '80px', height: '80px' }}>
                    <i className="fas fa-user-times fa-2x text-danger"></i>
                  </div>
                  <h5>Delete Team Member</h5>
                  <p className="text-muted mb-0">
                    Are you sure you want to permanently delete <strong>{deletingMember.name}</strong>?
                  </p>
                  <p className="text-danger small mt-2">
                    <i className="fas fa-exclamation-circle me-1"></i>
                    This action cannot be undone.
                  </p>
                </div>
              </>
            )}
          </Modal.Body>
          <Modal.Footer className="border-0">
            <Button variant="outline-secondary" onClick={() => setShowDeleteModal(false)}>
              Cancel
            </Button>
            <Button variant="danger" onClick={handleDelete}>
              <i className="fas fa-trash me-2"></i>
              Delete Permanently
            </Button>
          </Modal.Footer>
        </Modal>
      </Container>

      <style jsx="true">{`
        .table td, .table th {
          vertical-align: middle;
        }
        .table tbody tr:hover {
          background-color: rgba(0, 123, 255, 0.05);
        }
        .member-photo {
          transition: transform 0.3s ease;
        }
        .member-photo:hover {
          transform: scale(1.05);
        }
        .status-badge {
          cursor: pointer;
          transition: all 0.3s ease;
        }
        .status-badge:hover {
          opacity: 0.8;
          transform: scale(1.05);
        }
        .action-dropdown .btn-outline-primary {
          border-color: #dee2e6;
        }
        .action-dropdown .btn-outline-primary:hover {
          background-color: #0d6efd;
          border-color: #0d6efd;
          color: white;
        }
      `}</style>
    </>
  );
};

export default TeamMembersManager;